import { CloudDownload, FilterList, PictureAsPdf } from '@mui/icons-material';
import { Button, Chip, FormControlLabel, Switch, Tooltip } from '@mui/material';
import { Buffer } from 'buffer';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import fetch from '../../api/AxiosAdapter';
import Configuracao from '../../misc/Configuracao';
import { Peca } from '../../modelo/dominio/Peca';
import { Processo } from '../../modelo/Processo';
import { DadosProcessoDto } from '../../modelo/dto/DadosProcessoDto';
import { EnumOrigemProcesso } from '../../modelo/OrigemProcesso';
import DocumentoService from '../../services/DocumentoService';
import EscaninhoStore from '../../store/EscaninhoStore';
import ProcessoExpandidoStore from '../../store/ProcessoExpandidoStore';
import RouterStore from '../../store/RouterStore';
import FiltroStore from '../../store/FiltroStore';
import UsuarioStore from '../../store/UsuarioStore';
import MensagemStore from '../../store/MensagemStore';
import Ambiente from "../../utils/Ambiente";
import { apiErroPadraoJaExibido, apresentarApiErroPadrao, marcarApiErroPadraoExibido } from '../../utils/ApiErroPadraoUtils';
import UtilNumeroProcesso from '../../utils/UtilNumeroProcesso';
import { LinkPeca } from './LinkPeca';
import PecaDrawer from './PecaDrawer';
import './css/DadosProcessoStyles.css';

interface ViewPecasProcessoProps {
    pecas?: Peca[];
    idProcesso?: number;
    dadosProcesso?: DadosProcessoDto;
    carregandoPecas?: boolean;
    exibirSituacaoPeticao?: boolean;
}

export const ViewPecasProcesso = observer(class ViewPecasProcesso extends React.Component<ViewPecasProcessoProps> {
    apenasPecasPrincipais = false;
    apenasPeticoesPendentes = false;
    baixando = false;
    baixandoPecasFiltradas = false;
    pecaOpened = false;
    pecaBodyUrl;
    pecaViewerType: 'pdf' | 'html' = 'pdf';

    constructor(props: ViewPecasProcessoProps) {
        super(props);

        makeObservable(this, {
            apenasPecasPrincipais: observable,
            apenasPeticoesPendentes: observable,
            baixando: observable,
            baixandoPecasFiltradas: observable,
            pecaOpened: observable,
            pecaBodyUrl: observable,
            pecaViewerType: observable,
            toggleApenasPecasPrincipais: action,
            toggleApenasPeticoesPendentes: action,
            openPeca: action,
            openHtml: action,
            closePeca: action
        });
    }

    get idProcesso() {
        return this.props.idProcesso ||
            (ProcessoExpandidoStore.dadosProcesso?.idProcesso ?
                ProcessoExpandidoStore.dadosProcesso.idProcesso
                :
                RouterStore.params.idProcesso
            );
    }

    get processo() {
        return EscaninhoStore.processosPorTipoEscaninhoFiltrado.find(proc => proc.identificadorProcesso == this.idProcesso);
    }

    get isOrigemEsij(): boolean {
        return this.props.dadosProcesso?.origem === EnumOrigemProcesso.LEGADO
            || ProcessoExpandidoStore.dadosProcesso?.origem === EnumOrigemProcesso.LEGADO
            || this.processo?.ehLegado === true;
    }

    get numeroProcessoFormatado(): string {
        const numeroProcesso = this.props.dadosProcesso?.numero
            || ProcessoExpandidoStore.dadosProcesso?.numero
            || this.processo?.numeroProcesso;

        const numeroNormalizado = UtilNumeroProcesso.matchObjetoNumeroProcesso(numeroProcesso || '');
        return numeroNormalizado?.validoFormato ? numeroNormalizado.numeroCompleto : '';
    }

    get pecaDespachoAdmissibilidade(): Peca | undefined {
        if (!this.isOrigemEsij || !this.numeroProcessoFormatado) {
            return undefined;
        }

        return Peca.novo({
            id: -1,
            idProcesso: this.idProcesso,
            titulo: 'Despacho de Admissibilidade',
            tipoArquivo: 'HTML',
            principal: false,
            ativo: true,
            juntadoEm: undefined,
            ...({
                pecaDespachoAdmissibilidade: true,
                numeroProcesso: this.numeroProcessoFormatado
            } as any)
        });
    }

    get todasPecas(): Peca[] {
        const pecas = this.props.pecas || ProcessoExpandidoStore.pecas || [];
        return this.pecaDespachoAdmissibilidade
            ? [this.pecaDespachoAdmissibilidade, ...pecas]
            : pecas;
    }

    pecaPeticaoDentroDoIntervalo = (peca: Peca): boolean => {
        if (!peca.peticaoNaoLida) {
            return true;
        }
        if (!RouterStore.isEscaninhoPeticao || !FiltroStore.possuiIntervaloDataPeticao) {
            return true;
        }
        return FiltroStore.peticaoDentroDoIntervaloDeData(peca.juntadoEm ?? peca.criadoEm);
    }

    pecaPeticaoVisivelPorSituacao = (peca: Peca): boolean => {
        if (!peca.peticaoNaoLida) {
            return true;
        }
        if (!this.isOrigemEsij) {
            return true;
        }
        const situacao = peca.descricaoPeticaoStatus;
        if (Processo.situacaoPeticaoNaoPendente(situacao)) {
            return false;
        }
        const ehGabinete = !UsuarioStore.gabinete?.startsWith('SE');
        if (ehGabinete && Processo.situacaoPeticaoExclusivaSecretaria(situacao)) {
            return false;
        }
        return true;
    }

    get pecas(): Peca[] {
        return this.todasPecas
            .filter(peca => peca.principal || this.apenasPecasPrincipais === false)
            .filter(peca => peca.peticaoNaoLida || this.apenasPeticoesPendentes === false)
            .filter(peca => this.pecaPeticaoVisivelPorSituacao(peca))
            .filter(peca => this.pecaPeticaoDentroDoIntervalo(peca));
    }

    pecaVisivel = (peca: Peca): boolean => {
        return (peca.principal || this.apenasPecasPrincipais === false)
            && (peca.peticaoNaoLida || this.apenasPeticoesPendentes === false)
            && this.pecaPeticaoVisivelPorSituacao(peca)
            && this.pecaPeticaoDentroDoIntervalo(peca);
    }

    get totalPecas(): number {
        return this.todasPecas.length;
    }

    get carregandoPecas(): boolean {
        return this.props.carregandoPecas ?? (!this.props.pecas && ProcessoExpandidoStore.carregandoPecas);
    }

    get filtroAtivo(): boolean {
        return this.apenasPecasPrincipais || this.apenasPeticoesPendentes;
    }

    get filtroSemResultado(): boolean {
        return !this.carregandoPecas && this.filtroAtivo && this.pecas.length === 0;
    }

    get filtroNaoReduzLista(): boolean {
        return !this.carregandoPecas && this.filtroAtivo && this.totalPecas > 0 && this.pecas.length === this.totalPecas;
    }

    get mensagemFiltroSemResultado(): string {
        if (this.apenasPecasPrincipais) {
            return 'Não há peças principais para este processo.';
        }

        if (this.apenasPeticoesPendentes) {
            return 'Não há petições pendentes para este processo.';
        }

        return '';
    }

    get tooltipPecasFiltradas(): string {
        if (!this.filtroAtivo) {
            return 'Ative um filtro para baixar peças filtradas';
        }

        if (this.filtroSemResultado) {
            return 'Não há peças para baixar com o filtro atual';
        }

        if (this.filtroNaoReduzLista) {
            return 'O filtro atual inclui todas as peças do processo';
        }

        return 'Baixar apenas as peças visíveis na lista';
    }

    toggleApenasPecasPrincipais = (event: React.ChangeEvent, toggle: boolean) => {
        this.apenasPecasPrincipais = toggle;
        if (toggle) {
            this.apenasPeticoesPendentes = false;
        }
    };

    toggleApenasPeticoesPendentes = (event: React.ChangeEvent, toggle: boolean) => {
        this.apenasPeticoesPendentes = toggle;
        if (toggle) {
            this.apenasPecasPrincipais = false;
        }
    };

    openPeca = (blob) => {
        const newBlob = new Blob([blob], { type: "application/pdf" })

        if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
            (window.navigator as any).msSaveOrOpenBlob(newBlob);
            return;
        }

        const data = window.URL.createObjectURL(newBlob);
        this.pecaBodyUrl = data;
        this.pecaViewerType = 'pdf';

        this.pecaOpened = true;
    };

    openHtml = (blob) => {
        const newBlob = new Blob([blob], { type: "text/html;charset=utf-8" })

        if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
            (window.navigator as any).msSaveOrOpenBlob(newBlob);
            return;
        }

        const data = window.URL.createObjectURL(newBlob);
        this.pecaBodyUrl = data;
        this.pecaViewerType = 'html';

        this.pecaOpened = true;
    };

    handleClickBaixarPecasFiltradas = async () => {
        if (this.carregandoPecas || !this.filtroAtivo || this.filtroSemResultado || this.filtroNaoReduzLista || this.baixandoPecasFiltradas) {
            return;
        }

        runInAction(() => {
            this.baixandoPecasFiltradas = true;
        });

        try {
            const pecasConcatenadas = await DocumentoService.buscarPecasFiltradas(this.idProcesso, this.pecas.map(peca => peca.id));
            const newBlob = new Blob([pecasConcatenadas as BlobPart], { type: "application/pdf" })
            const ehPdf = await this.blobEhPdf(newBlob);

            if (!ehPdf) {
                const respostaTexto = await newBlob.text();
                console.error('Resposta inválida ao baixar peças filtradas.', respostaTexto);
                MensagemStore.apresentarErro('Não foi possível baixar as peças filtradas. O servidor retornou um arquivo inválido.');
                return;
            }

            if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                (window.navigator as any).msSaveOrOpenBlob(newBlob);
                return;
            }
            const numeroProcesso = this.processo?.numeroProcesso || this.props.dadosProcesso?.numero || this.idProcesso;
            const data = window.URL.createObjectURL(newBlob);
            const link = document.createElement('a');
            link.href = data;
            link.download = "pecas_" + numeroProcesso + ".pdf";
            link.click();
            setTimeout(function () {
                window.URL.revokeObjectURL(data);
            }, 100);
        } finally {
            runInAction(() => {
                this.baixandoPecasFiltradas = false;
            });
        }
    }

    blobEhPdf = async (blob: Blob): Promise<boolean> => {
        const assinatura = await blob.slice(0, 4).text();
        return assinatura === '%PDF';
    }

    handleClickPdfUnificado = async () => {
        if (this.idProcesso.toString().length === 12) {
            const pdfUnificadoUrl = Configuracao.urlProcessosGE + this.idProcesso + '/processo-unificado.pdf';
            runInAction(() => {
                this.baixando = true;
            });

            const baixarInteiroTeor = async (silencioso: boolean) => {
                const resposta = await fetch(pdfUnificadoUrl, {
                    method: 'GET',
                    responseType: "blob",
                    silent: silencioso
                });
                return resposta.data;
            };

            try {
                let blob;
                try {
                    blob = await baixarInteiroTeor(true);
                } catch {
                    blob = await baixarInteiroTeor(false);
                }

                this.showFile(blob, 'InteiroTeor');
            } catch (error) {
                const erroBlob = error?.response?.data;
                if (erroBlob instanceof Blob) {
                    try {
                        const conteudoTexto = await erroBlob.text();
                        const json = JSON.parse(conteudoTexto);
                        if (apresentarApiErroPadrao(json)) {
                            marcarApiErroPadraoExibido(error);
                            return;
                        }
                    } catch (_e) {
                        // segue para fallback abaixo
                    }
                }

                if (!apiErroPadraoJaExibido(error) && !apresentarApiErroPadrao(error)) {
                    MensagemStore.apresentarErro('Erro ao baixar inteiro teor do processo.');
                }
            } finally {
                runInAction(() => {
                    this.baixando = false;
                });
            }
        } else {
            try {

                // ex. https://pje-hml.tst.jus.br/pje-comum-api/api/processos/id/35200/documentos/agrupados?processoCompleto=true
                const urlRedirect = Ambiente.urlPJe.split("pjekz")[0] + "pje-comum-api/api/processos/id/" + this.idProcesso + '/documentos/agrupados?processoCompleto=true';
                const buffLogus = Buffer.from(UsuarioStore.token.split('.')[0], 'base64');
                const tokenLogus = JSON.parse(buffLogus.toString());
                const buffTokenKz = Buffer.from((tokenLogus.tokens.kz.token.split("."))[1], 'base64');
                const idPerfil = JSON.parse(buffTokenKz.toString()).perfil;
                const refreshToken = tokenLogus.tokens.kz_refresh.token;

                const redirectPageWithSSO = Ambiente.urlPJe.split("pjekz")[0] + "btv/bemtevi/tst-sso.html?id_perfil=" + idPerfil + "&redirect_url=" + urlRedirect + "&refresh_token=" + refreshToken;

                window.open(redirectPageWithSSO);
            } catch (_e) {
                console.error("erro ao processar o token: token=" + UsuarioStore.token)
            }
        }
    }

    showFile = (blob, nome) => {
        const newBlob = new Blob([blob], { type: "application/pdf" })
        if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
            (window.navigator as any).msSaveOrOpenBlob(newBlob);
            return;
        }
        const data = window.URL.createObjectURL(newBlob);
        const link = document.createElement('a');
        link.href = data;
        link.download = nome + ".pdf";
        link.click();
        setTimeout(function () {
            window.URL.revokeObjectURL(data);
        }, 100);
    }

    closePeca = () => {
        this.pecaOpened = false;
    };

    render() {
        const todasPecas = this.todasPecas;
        const pecasFiltradas = todasPecas.filter(this.pecaVisivel);
        const totalPecas = todasPecas.length;

        return (
            <div className='divPecas'>
                <div className='divPeca'>
                    <div className='areaFixa'>
                        <div className='areaTituloPecas'>
                            <div>
                                <div className='linhaTituloListaPecas'>
                                    <div className='tituloListaPecas'>Peças do processo</div>
                                    <Chip
                                        className='contadorPecas'
                                        label={`${pecasFiltradas.length}/${totalPecas}`}
                                        size='small'
                                        icon={<FilterList />}
                                    />
                                </div>
                                <div className='subtituloListaPecas'>Clique em uma peça para visualizar o documento.</div>
                            </div>
                        </div>
                        <div className='areaFiltrosPecas'>
                            <FormControlLabel
                                className='filtroPecas'
                                control={
                                    <Switch
                                        checked={this.apenasPecasPrincipais}
                                        onChange={this.toggleApenasPecasPrincipais}
                                        size='small'
                                    />
                                }
                                label='Apenas principais'
                            />
                            <FormControlLabel
                                className='filtroPecas'
                                control={
                                    <Switch
                                        checked={this.apenasPeticoesPendentes}
                                        onChange={this.toggleApenasPeticoesPendentes}
                                        size='small'
                                    />
                                }
                                label='Apenas petições pendentes'
                            />
                        </div>
                        <div className='areaAcoesPecas'>
                            <Tooltip title={'Baixar inteiro teor do processo'}>
                                <span>
                                    <Button
                                        className='botaoDownloadPdf'
                                        variant='contained'
                                        color='primary'
                                        startIcon={<PictureAsPdf />}
                                        onClick={this.handleClickPdfUnificado}
                                        disabled={this.baixando}
                                    >
                                        {this.baixando ? "Aguarde..." : "Inteiro Teor"}
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title={this.tooltipPecasFiltradas}>
                                <span>
                                    <Button
                                        className='botaoDownloadPdf'
                                        variant='outlined'
                                        color='primary'
                                        startIcon={<CloudDownload />}
                                        onClick={this.handleClickBaixarPecasFiltradas}
                                        disabled={this.carregandoPecas || this.baixandoPecasFiltradas || !this.filtroAtivo || this.filtroSemResultado || this.filtroNaoReduzLista}
                                    >
                                        {this.baixandoPecasFiltradas ? "Aguarde..." : "Peças filtradas"}
                                    </Button>
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                    <div className='listaPecas'>
                        <div className='areaPecas'>
                            {this.carregandoPecas &&
                                <div className='mensagemListaPecasVazia'>
                                    <div id='html-spinner'></div>
                                    <span>Carregando peças do processo...</span>
                                </div>
                            }
                            {this.filtroSemResultado &&
                                <div className='mensagemListaPecasVazia'>
                                    {this.mensagemFiltroSemResultado}
                                </div>
                            }
                            {!this.carregandoPecas && todasPecas && todasPecas.map((peca, index) =>
                                <div className={`itemPeca ${this.pecaVisivel(peca) ? '' : 'itemPeca--oculta'}`} key={peca.id || index}>
                                    {peca.tipoArquivo === 'PDF' ?
                                        <LinkPeca peca={peca} dadosProcesso={this.props.dadosProcesso} openPeca={this.openPeca} openHTML={this.openHtml} exibirSituacaoPeticao={this.props.exibirSituacaoPeticao} />
                                        :
                                        <LinkPeca peca={peca} dadosProcesso={this.props.dadosProcesso} openPeca={this.openHtml} openHTML={this.openHtml} exibirSituacaoPeticao={this.props.exibirSituacaoPeticao} />
                                    }
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <PecaDrawer
                    pecaBodyUrl={this.pecaBodyUrl}
                    pecaOpened={this.pecaOpened}
                    closePeca={this.closePeca}
                    viewerType={this.pecaViewerType}
                />
            </div>
        )
    }
});
